/**
 * DrawerHandle Component
 *
 * Draggable handle for the Layer Drawer with smooth resize gestures.
 * Supports both touch and mouse interactions.
 */

import React, { useRef, useCallback, useEffect } from 'react';
import './DrawerHandle.scss';
import { DrawerHeight, calculateDrawerHeight, clamp } from '../utils/layerDrawerUtils';

interface DrawerHandleProps {
  currentHeight: DrawerHeight;
  onHeightChange: (height: DrawerHeight) => void;
}

export const DrawerHandle: React.FC<DrawerHandleProps> = ({
  currentHeight,
  onHeightChange
}) => {
  const handleRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    isDragging: boolean;
    startY: number;
    startHeight: number;
  }>({
    isDragging: false,
    startY: 0,
    startHeight: 0
  });

  /**
   * Determine drawer height state from pixel value
   */
  const getHeightState = useCallback((pixels: number, viewportHeight: number): DrawerHeight => {
    const ratio = pixels / viewportHeight;

    // Snap to states based on thresholds
    if (pixels < 150) {
      return DrawerHeight.COLLAPSED;
    } else if (ratio < 0.65) {
      return DrawerHeight.HALF;
    } else {
      return DrawerHeight.FULL;
    }
  }, []);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((clientY: number) => {
    const viewportHeight = window.innerHeight;
    const currentPixels = calculateDrawerHeight(currentHeight, viewportHeight);

    dragStateRef.current = {
      isDragging: true,
      startY: clientY,
      startHeight: currentPixels
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [currentHeight]);

  /**
   * Handle drag move
   */
  const handleDragMove = useCallback((clientY: number) => {
    if (!dragStateRef.current.isDragging) return;

    const viewportHeight = window.innerHeight;
    const deltaY = dragStateRef.current.startY - clientY; // Inverted (drag up = increase)
    const newPixels = clamp(
      dragStateRef.current.startHeight + deltaY,
      DrawerHeight.COLLAPSED,
      viewportHeight * DrawerHeight.FULL
    );

    const newState = getHeightState(newPixels, viewportHeight);
    if (newState !== currentHeight) {
      onHeightChange(newState);
    }
  }, [currentHeight, onHeightChange, getHeightState]);

  /**
   * Handle drag end
   */
  const handleDragEnd = useCallback(() => {
    dragStateRef.current.isDragging = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  /**
   * Mouse event handlers
   */
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientY);
  }, [handleDragStart]);

  const onMouseMove = useCallback((e: MouseEvent) => {
    handleDragMove(e.clientY);
  }, [handleDragMove]);

  const onMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  /**
   * Touch event handlers
   */
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientY);
    }
  }, [handleDragStart]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 1) {
      handleDragMove(e.touches[0].clientY);
    }
  }, [handleDragMove]);

  const onTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  /**
   * Click handler for quick expand/collapse
   */
  const onClick = useCallback(() => {
    if (dragStateRef.current.isDragging) return;

    // Cycle through states
    if (currentHeight === DrawerHeight.COLLAPSED) {
      onHeightChange(DrawerHeight.HALF);
    } else if (currentHeight === DrawerHeight.HALF) {
      onHeightChange(DrawerHeight.FULL);
    } else {
      onHeightChange(DrawerHeight.COLLAPSED);
    }
  }, [currentHeight, onHeightChange]);

  /**
   * Setup global event listeners
   */
  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  return (
    <div
      ref={handleRef}
      className="layer-drawer-handle"
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label="Resize layer drawer"
    >
      <div className="layer-drawer-handle-bar" />
    </div>
  );
};
